<script setup>
import {ref} from 'vue';
import {router} from '@inertiajs/vue3';
import {useForm} from '@inertiajs/vue3';
import JetButton from '@/Jetstream/Button.vue';
import JetFormSection from '@/Jetstream/FormSection.vue';
import JetInput from '@/Jetstream/Input.vue';
import JetInputError from '@/Jetstream/InputError.vue';
import JetLabel from '@/Jetstream/Label.vue';
import JetActionMessage from '@/Jetstream/ActionMessage.vue';
import JetSecondaryButton from '@/Jetstream/SecondaryButton.vue';
import JetSelect from "@/Jetstream/Select";
import JetCheckbox from "@/Jetstream/Checkbox";
import {useLanguageSelectList} from "../../../Composables/languageSelectList";

const props = defineProps({
    user: Object,
});

const form = useForm({
    _method: 'PUT',
    user_id: props.user.id,
    salutation_id: props.user.salutation_id,
    name: props.user.name,
    email: props.user.email,
    additional_email: props.user.additional_email,
    preferred_locale: props.user.preferred_locale,
    is_active: props.user.is_active,
    photo: null,
});

const languages = useLanguageSelectList();

const photoPreview = ref(null);
const photoInput = ref(null);

const updateProfileInformation = () => {
    if (photoInput.value) {
        form.photo = photoInput.value.files[0];
    }

    form.post(route('user.profile-info.update'), {
        errorBag: 'updateProfileInformation',
        preserveScroll: true,
        onSuccess: () => clearPhotoFileInput(),
    });
};

const selectNewPhoto = () => {
    photoInput.value.click();
};

const updatePhotoPreview = () => {
    const photo = photoInput.value.files[0];

    if (!photo) return;

    const reader = new FileReader();

    reader.onload = (e) => {
        photoPreview.value = e.target.result;
    };

    reader.readAsDataURL(photo);
};

const deletePhoto = () => {
    router.delete(route('customer.profile-picture.destroy', {customer: props.user.id}), {
        preserveScroll: true,
        onSuccess: () => {
            photoPreview.value = null;
            clearPhotoFileInput();
        },
    });
};

const clearPhotoFileInput = () => {
    if (photoInput.value?.value) {
        photoInput.value.value = null;
    }
};
</script>

<template>
    <JetFormSection @submitted="updateProfileInformation">
        <template #title>
            {{ $t('Profile Information') }}
        </template>

        <template #description>
        </template>

        <template #form>
            <!-- Profile Photo -->
            <div v-if="$page.props.jetstream.managesProfilePhotos" class="col-span-6 sm:col-span-4">
                <!-- Profile Photo File Input -->
                <input
                    ref="photoInput"
                    type="file"
                    class="hidden"
                    @change="updatePhotoPreview"
                >

                <JetLabel for="photo" :value="$t('Photo')"/>

                <!-- Current Profile Photo -->
                <div v-show="! photoPreview" class="mt-2">
                    <img :src="user.profile_photo_url" :alt="user.name" class="rounded-full h-20 w-20 object-cover">
                </div>

                <!-- New Profile Photo Preview -->
                <div v-show="photoPreview" class="mt-2">
                    <span
                        class="block rounded-full w-20 h-20 bg-cover bg-no-repeat bg-center"
                        :style="'background-image: url(\'' + photoPreview + '\');'"
                    />
                </div>

                <JetSecondaryButton class="mt-2 mr-2" type="button" @click.prevent="selectNewPhoto">
                    {{ $t('Select a new photo') }}
                </JetSecondaryButton>

                <JetSecondaryButton
                    v-if="user.profile_photo_path"
                    type="button"
                    class="mt-2"
                    @click.prevent="deletePhoto"
                >
                    {{ $t('Remove Photo') }}
                </JetSecondaryButton>

                <JetInputError :message="form.errors.photo" class="mt-2"/>
            </div>

            <!-- Salutation -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="salutation" :value="$t('Salutation')"/>
                <JetSelect id="salutation" class="mt-1 block w-full"
                           :show-please-choose="true"
                           :items="$page.props.salutations"
                           v-model="form.salutation_id"
                />
                <JetInputError :message="form.errors.salutation_id"
                               class="mt-2"/>
            </div>

            <!-- Name -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="name" :value="$t('Name')"/>
                <JetInput
                    id="name"
                    v-model="form.name"
                    type="text"
                    class="mt-1 block w-full"
                    autocomplete="name"
                />
                <JetInputError :message="form.errors.name" class="mt-2"/>
            </div>

            <!-- Email -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="email" :value="$t('Email')"/>
                <JetInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="mt-1 block w-full"
                />
                <JetInputError :message="form.errors.email" class="mt-2"/>
            </div>

            <!-- Additional E-Mail -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="additional_email" :value="$t('Additional email address (CC in notifications)')"/>
                <JetInput
                    id="additional_email"
                    v-model="form.additional_email"
                    type="email"
                    class="mt-1 block w-full"
                />
                <JetInputError :message="form.errors.additional_email" class="mt-2"/>
            </div>

            <!-- Preferred locale -->
            <div class="col-span-6 sm:col-span-4">
                <JetLabel for="preferred_locale" :value="$t('Preferred language for communication')"/>
                <JetSelect id="preferred_locale" class="mt-1 block w-full"
                           :show-please-choose="true"
                           :items="languages"
                           v-model="form.preferred_locale"
                />
                <JetInputError :message="form.errors.preferred_locale"
                               class="mt-2"/>
            </div>

            <div class="col-span-6 sm:col-span-4 pt-3">
                <label class="flex items-center">
                    <jet-checkbox name="is_active" v-model:checked="form.is_active"/>
                    <span
                        class="ml-2 text-sm text-gray-600">{{ $t('User is active') }}</span>
                </label>
            </div>
        </template>

        <template #actions>
            <JetActionMessage :on="form.recentlySuccessful" class="mr-3">
                {{ $t('Saved.') }}
            </JetActionMessage>

            <JetButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing">
                {{ $t('Save') }}
            </JetButton>
        </template>
    </JetFormSection>
</template>
